import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  FormControlLabel,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Switch,
  IconButton,
  Tooltip,
} from '@mui/material';
import { DragDropContext } from 'react-beautiful-dnd';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';

import {
  Formation,
  FormationTheme,
  Player,
  Position,
  Team,
  TeamName,
  TeamResult,
  ViewMode,
} from '../types';
import TeamCard from './TeamCard';
import {
  objectToBase64,
  base64ToObject,
  getFormationPositions,
  drawFormations,
} from '../utils';
import FormationView from './FormationView';
import { AddCircleOutline } from '@mui/icons-material';
import { AddPlayerDialog } from './AddPlayerDialog';

export default function Result() {
  const { resultCode } = useParams<{ resultCode: string }>();
  const [result, setResult] = useState<TeamResult | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.List);
  const [hidePositions, setHidePositions] = useState(false);
  const [showAddPlayer, setShowAddPlayer] = useState(false);

  const navigate = useNavigate();

  const reload = () => {
    const newCode = objectToBase64(result);
    setResult({ ...result! });
    navigate(`/results/${newCode}`);
  };

  const findTeam = (team: TeamName) => {
    if (!result) {
      return null;
    }
    return result.teams.find(({ name }) => name === team);
  };

  useEffect(() => {
    try {
      const result = base64ToObject<TeamResult>(resultCode);
      setResult(result);
      setViewMode(result?.viewMode ?? ViewMode.List);
    } catch (error: any) {
      setError(error);
    }
  }, [resultCode]);

  const handleDeletePlayer = (playerName: string, teamName: TeamName) => {
    try {
      const team = findTeam(teamName);
      if (!team) {
        return;
      }
      const index = team.players.findIndex(({ name }) => name === playerName);
      if (index >= 0) {
        team.players.splice(index, 1);
      }
      reload();
    } catch (error) {
      console.error(error);
    }
  };

  const handleMovePlayer = (
    playerName: string,
    fromTeamName: TeamName,
    toTeamName: TeamName
  ) => {
    try {
      const fromTeam = findTeam(fromTeamName);
      const toTeam = findTeam(toTeamName);

      if (!fromTeam || !toTeam) {
        return;
      }

      const index = fromTeam.players.findIndex(
        ({ name }) => name === playerName
      );
      if (index < 0) {
        return;
      }

      const removedPlayers = fromTeam.players.splice(index, 1);
      toTeam.players.push(removedPlayers[0]);
      reload();
    } catch (error) {
      console.error(error);
    }
  };

  const handleSetPlayerPosition = (player: Player, position?: Position) => {
    try {
      player.position = position;
      reload();
    } catch (error) {
      console.error(error);
    }
  };

  const handleDragPlayer = (result: any) => {
    if (!result.destination) {
      return;
    }
    const { index: sourceIndex, droppableId: sourceTeamName } = result.source;
    const { index: destIndex, droppableId: destTeamName } = result.destination;
    const sourceTeam = findTeam(sourceTeamName as TeamName);
    const destTeam = findTeam(destTeamName as TeamName);

    if (!sourceTeam || !destTeam) {
      return;
    }

    destTeam.players.splice(
      destIndex,
      0,
      sourceTeam.players.splice(sourceIndex, 1)[0]
    );
    reload();
  };

  const handleChangeViewMode = (
    _event: React.MouseEvent<HTMLElement>,
    nextView: string
  ) => {
    setViewMode(nextView as any);
    result!.viewMode = nextView as ViewMode;

    if (nextView === 'formation') {
      drawFormations(result!);
    }
    reload();
  };

  const handleSwitchPlayerPosition = (
    player: Player,
    fromTeam: Team,
    toTeam: Team,
    position?: Position
  ) => {
    let destPlayer: Player | undefined;
    if (position) {
      destPlayer = toTeam.players.find(({ position: p }) => p === position);
      if (destPlayer) {
        destPlayer.position = player.position;
      }
    }
    player.position = position;

    // Change teams for players
    if (fromTeam !== toTeam) {
      const fromIndex = fromTeam.players.findIndex((p) => p === player);
      if (fromIndex >= 0) {
        if (destPlayer) {
          fromTeam.players.splice(fromIndex, 1, destPlayer);
        } else {
          // Moving to the bench
          fromTeam.players.splice(fromIndex, 1);
          toTeam.players.push(player);
        }
      }
      const toIndex = toTeam.players.findIndex((p) => p === destPlayer);
      if (toIndex >= 0) {
        toTeam.players.splice(toIndex, 1, player);
      }
    }
    reload();
  };

  const handleRemovePlayer = (player: Player, team: Team) => {
    const index = team.players.findIndex((p) => p === player);
    if (index >= 0) {
      team.players.splice(index, 1);
    }
    reload();
  };

  const handleTeamFormationChange = (team: Team, formation: Formation) => {
    const positions = getFormationPositions(formation)?.map(
      ({ position }) => position
    );
    if (!positions) {
      return;
    }
    const filledPositions = team.players
      .map(({ position }) => position)
      .filter((position) => position && positions.includes(position));
    const unfilledPositions = positions.filter(
      (position) => !filledPositions.includes(position)
    );

    // Reassign new player positions
    team.players.forEach((player) => {
      if (!player.position || !positions.includes(player.position)) {
        player.position = unfilledPositions.shift();
      }
    });
    team.formation = formation;
    reload();
  };

  const handleHidePositionsChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setHidePositions(event.target.checked);
  };

  const handleJerseyChange = (team: Team, theme: FormationTheme) => {
    team.theme = theme;
    reload();
  };

  const handleAddPlayer = (player: string, teamName: TeamName) => {
    const team = findTeam(teamName);
    if (!team) {
      return;
    }
    team.players.push({ name: player });
    setShowAddPlayer(false);
    reload();
  };

  const handleSetTeamCustomName = (team: Team, customName: string) => {
    team.customName = customName;
    reload();
  };

  if (!result) {
    return <Typography>Loading...</Typography>;
  }

  if (error) {
    return <Typography>Invalid code</Typography>;
  }

  return (
    <Stack
      direction="column"
      sx={{
        width: '100%',
        height: '100%',
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          {`Date: ${result.date}`}
        </Typography>
        <Stack direction="row" spacing={2}>
          {viewMode === 'list' && (
            <FormControlLabel
              control={
                <Switch
                  checked={hidePositions}
                  onChange={handleHidePositionsChange}
                />
              }
              label="Hide positions"
            />
          )}
          <Tooltip title="Add player">
            <IconButton
              aria-label="add"
              color="primary"
              onClick={() => setShowAddPlayer(true)}
            >
              <AddCircleOutline />
            </IconButton>
          </Tooltip>
          <ToggleButtonGroup
            value={viewMode}
            size="small"
            exclusive
            onChange={handleChangeViewMode}
          >
            <Tooltip title="List view">
              <ToggleButton value="list">
                <ViewListIcon />
              </ToggleButton>
            </Tooltip>
            <Tooltip title="Formation view">
              <ToggleButton value="formation">
                <ViewModuleIcon />
              </ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>
        </Stack>
      </Stack>

      {viewMode === ViewMode.List && (
        <Stack
          direction={{
            xs: 'column',
            sm: 'column',
            md: 'column',
            lg: 'row',
          }}
          sx={{
            justifyContent: 'space-between',
          }}
        >
          <DragDropContext onDragEnd={handleDragPlayer}>
            {result.teams.map((team, index) => (
              <TeamCard
                team={team}
                key={index}
                canMoveTo={result.teams.map(({ name }) => name)}
                onDeletePlayer={handleDeletePlayer}
                onMovePlayer={handleMovePlayer}
                onSetPlayerPosition={handleSetPlayerPosition}
                onSetCustomTeamName={handleSetTeamCustomName}
                hidePositions={hidePositions}
              />
            ))}
          </DragDropContext>
        </Stack>
      )}

      {viewMode === ViewMode.Formation && (
        <FormationView
          teams={result.teams}
          onJerseyChange={handleJerseyChange}
          onFormationChange={handleTeamFormationChange}
          onSwitchPlayerPosition={handleSwitchPlayerPosition}
          onRemovePlayer={handleRemovePlayer}
          onSetCustomTeamName={handleSetTeamCustomName}
        />
      )}
      {showAddPlayer && (
        <AddPlayerDialog
          teams={result.teams}
          onClose={() => setShowAddPlayer(false)}
          onSubmit={handleAddPlayer}
        />
      )}
    </Stack>
  );
}
